import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import leftFillNum from "../../../utils/leftFillNum";
import Button from "../../generic/buttons/Button";
import StatusLabel from "../../generic/dataDisplay/StatusLabel";
import OrderInfo from "../Order/components/OrderInfo";
import {
  cancelOrder,
  driverAcceptOrder,
  driverFinishOrder,
  getOrder,
  getOrderPaymentTypes,
  getOrderStatuses,
} from "../../../store/actions/orderActions";
import dayjs from "dayjs";
import numWord from "../../../utils/numWord";
import {
  USER_ROLE_ADMIN,
  USER_ROLE_DRIVER,
  USER_ROLE_MANAGER,
} from "../../../utils/constants/userRoles";
import classes from "./OrderDetail.module.scss";

interface IOrderDetailProps {
  modalEditToggle: Dispatch<SetStateAction<boolean>>;
  modalAcceptToggle: Dispatch<SetStateAction<boolean>>;
  modalFinishToggle: Dispatch<SetStateAction<boolean>>;
  modalCancelToggle: Dispatch<SetStateAction<boolean>>;
}

function OrderDetail({
  modalEditToggle,
  modalAcceptToggle,
  modalFinishToggle,
  modalCancelToggle,
}: IOrderDetailProps) {
  const dispatch = useAppDispatch();
  const { order, isLoadingOrder } = useAppSelector((state) => state.order);
  const { role_id } = useAppSelector((state) => state.auth.user);
  const isManagement =
    role_id === USER_ROLE_ADMIN || role_id === USER_ROLE_MANAGER;
  const isOrderCancelable = order.status_id !== 7 && order.status_id !== 6;
  const isDriver = role_id === USER_ROLE_DRIVER;

  const curDate = new Date();
  const birthDate = new Date(order.client_birthday);
  const diffBirthDate = curDate.getFullYear() - birthDate.getFullYear();
  const formattedBirthday = dayjs(order.client_birthday, "YYYY-MM-DD").format(
    "DD.MM.YYYY"
  );
  const yearsCount = `${diffBirthDate} ${numWord(diffBirthDate, [
    "год",
    "года",
    "лет",
  ])}`;
  const birthDateTemplate = !isLoadingOrder && (
    <>
      {formattedBirthday} <span>({yearsCount})</span>
    </>
  );

  useEffect(() => {
    dispatch(getOrderPaymentTypes());
    dispatch(getOrderStatuses());
  }, [dispatch]);

  const orderAcceptHandler = async (id: number) => {
    const acceptedID = await dispatch(driverAcceptOrder(id));
    if (acceptedID) {
      await dispatch(getOrder({ id }));
      modalAcceptToggle(true);
    }
  };

  const orderFinishHandler = async (id: number) => {
    const finishedID = await dispatch(driverFinishOrder(id));
    if (finishedID) {
      await dispatch(getOrder({ id }));
      modalFinishToggle(true);
    }
  };

  const orderCancelHandler = async (id: number) => {
    const isCanceled = await dispatch(cancelOrder(id));
    if (isCanceled) {
      await dispatch(getOrder({ id }));
      modalCancelToggle(true);
    }
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.top}>
        <div className={classes.top_title}>
          <span>{`Заявка №${leftFillNum(order.id)}`}</span>
          <StatusLabel id={order.status_id} type={"order"} />
        </div>
        <div className={classes.top_buttons}>
          {isManagement && (
            <Button
              color={"white"}
              size={"sm"}
              onClick={() => modalEditToggle(true)}
              className={classes.button_edit}
            >
              Редактировать
            </Button>
          )}
          {isManagement && isOrderCancelable && (
            <Button
              color={"white"}
              size={"sm"}
              onClick={() => orderCancelHandler(order.id)}
              className={classes.button_edit}
            >
              Отменить заявку
            </Button>
          )}
          {isDriver && order.status_id === 2 && (
            <Button
              size={"sm"}
              onClick={() => orderAcceptHandler(order.id)}
              className={classes.button_accept}
            >
              Подтвердить заявку
            </Button>
          )}
          {isDriver && order.status_id === 4 && (
            <Button
              size={"sm"}
              onClick={() => orderFinishHandler(order.id)}
              className={classes.button_finish}
            >
              Завершить
            </Button>
          )}
        </div>
      </div>
      <div className={classes.client}>
        <div className={classes.client_title}>
          {order.client_fio || "Без имени"}
        </div>
        <div className={classes.client_list}>
          {!isDriver && (
            <>
              <div className={classes.client_item}>
                <div className={classes.client_item_label}>Телефон</div>
                <div
                  className={[
                    classes.client_item_value,
                    classes.client_item_value_phone,
                  ].join(" ")}
                >
                  {order.client_phone}
                </div>
              </div>
            </>
          )}
          {/*         <div className={classes.client_item}>
            <div className={classes.client_item_label}>Дата рождения:</div>
            <div
              className={[
                classes.client_item_value,
                classes.client_item_value_birthdate,
              ].join(" ")}
            >
              {birthDateTemplate}
            </div>
          </div> */}
          {order.client_height && (
            <div className={classes.client_client_item}>
              <div className={classes.client_item_label}>Рост:</div>
              <div
                className={[
                  classes.client_item_value,
                  classes.client_item_value_height,
                ].join(" ")}
              >
                {!isLoadingOrder &&
                  (order.client_height ? `${order.client_height} см` : `-`)}
              </div>
            </div>
          )}
          {order.client_weight && (
            <div className={classes.client_item}>
              <div className={classes.client_item_label}>Вес:</div>
              <div className={classes.client_item_value}>
                {!isLoadingOrder &&
                  (order.client_weight ? `${order.client_weight} кг` : `-`)}
              </div>
            </div>
          )}
        </div>
      </div>
      <OrderInfo />
    </div>
  );
}

export default OrderDetail;
